import React, { useCallback, useState } from 'react';

const BG = 'var(--bg)';
const CARD = 'var(--card)';
const BORDER = 'var(--border)';
const TEXT = 'var(--text)';
const MUTED = 'var(--muted)';
const ACCENT = '#10B981';
const PURPLE = '#8B5CF6';
const CYAN = '#06B6D4';

type SimResults = {
  predicted_reach: number;
  expected_leads: number;
  cost_per_lead: number;
  success_probability: number;
  best_channel: string;
};

const CAMPAIGN_TYPES = ['Social Media', 'Email', 'WhatsApp', 'Google Ads', 'All Channels'] as const;
const GOALS = ['Leads', 'Sales', 'Awareness', 'App Installs'] as const;

function formatINR(n: number): string {
  return `₹${n.toLocaleString('en-IN')}`;
}

export function CampaignSimulatorPage() {
  const [campaignType, setCampaignType] = useState<string>('Social Media');
  const [audience, setAudience] = useState('');
  const [budget, setBudget] = useState(15000);
  const [message, setMessage] = useState('');
  const [goal, setGoal] = useState('Leads');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<SimResults | null>(null);

  const simulateCampaign = useCallback(async () => {
    setBusy(true);
    setResults(null);
    await new Promise((r) => setTimeout(r, 2000));

    const predicted_reach =
      campaignType === 'Google Ads' ? budget * 12 : budget * 8;
    const expected_leads = Math.round(predicted_reach * 0.023);
    const cost_per_lead = Math.round(budget / (expected_leads || 1));
    const success_probability = Math.min(95, 60 + budget / 2000);
    const best_channel = campaignType === 'All Channels' ? 'WhatsApp' : campaignType;

    setResults({
      predicted_reach,
      expected_leads,
      cost_per_lead,
      success_probability,
      best_channel,
    });
    setBusy(false);
  }, [budget, campaignType]);

  const pillBase: React.CSSProperties = {
    padding: '8px 14px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    border: `1px solid ${BORDER}`,
    background: CARD,
    color: MUTED,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  const pillActive: React.CSSProperties = {
    border: `1px solid ${ACCENT}`,
    background: `${ACCENT}22`,
    color: TEXT,
  };

  return (
    <div className="odc-page relative z-10 space-y-8">
      {/* HEADER */}
      <header
        className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between"
        style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 24 }}
      >
        <div className="min-w-0 flex-1 space-y-3">
          <span
            className="inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{ background: `${PURPLE}28`, color: '#C4B5FD', border: `1px solid ${PURPLE}55` }}
          >
            AI SIMULATOR
          </span>
          <h1 className="text-2xl font-bold md:text-3xl" style={{ color: TEXT }}>
            Campaign Simulator
          </h1>
          <p className="max-w-xl text-sm leading-relaxed" style={{ color: MUTED }}>
            Test your campaign on 10,000 AI-generated customers before spending a single rupee
          </p>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {['10,000 Virtual Customers', '2 min Simulation', '89% Accuracy'].map((t) => (
            <span
              key={t}
              className="odc-badge shrink-0 rounded-full !px-3 !py-2 !text-[11px]"
              style={{
                background: `${CYAN}14`,
                color: CYAN,
                border: `1px solid ${CYAN}40`,
              }}
            >
              {t}
            </span>
          ))}
        </div>
      </header>

      {/* HOW IT WORKS */}
      <section>
        <div className="grid gap-4 md:grid-cols-3">
          {(
            [
              { icon: '🎯', title: 'Define', body: 'Set your audience, budget & message' },
              { icon: '🧪', title: 'Simulate', body: 'AI tests on 10,000 virtual customers' },
              { icon: '📊', title: 'Predict', body: 'Get conversion, reach & ROI forecast' },
            ] as const
          ).map((c) => (
            <div key={c.title} className="odc-card !p-6" style={{ transform: 'none' }}>
              <div className="text-3xl">{c.icon}</div>
              <h3 className="mt-3 text-lg font-bold" style={{ color: TEXT }}>
                {c.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: MUTED }}>
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* MAIN 60/40 */}
      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        {/* LEFT — Form */}
        <div className="odc-card space-y-6 !p-7" style={{ transform: 'none' }}>
          <h2 className="text-lg font-bold" style={{ color: TEXT }}>
            Configure Your Campaign
          </h2>

          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Campaign Type
            </label>
            <div className="flex flex-wrap gap-2">
              {CAMPAIGN_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setCampaignType(t)}
                  style={{ ...pillBase, ...(campaignType === t ? pillActive : {}) }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Target Audience
            </label>
            <input
              className="odc-input w-full"
              placeholder="e.g. 30-45 year old men in Delhi interested in real estate"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <label className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
                Monthly Budget
              </label>
              <span className="text-sm font-bold" style={{ color: ACCENT }}>
                {formatINR(budget)}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-semibold" style={{ color: MUTED }}>
                ₹1K
              </span>
              <input
                type="range"
                min={1000}
                max={100000}
                step={1000}
                value={budget}
                onChange={(e) => setBudget(Number(e.target.value))}
                className="h-2 w-full min-w-0 flex-1 cursor-pointer rounded-full"
                style={{ accentColor: ACCENT }}
              />
              <span className="text-[10px] font-semibold" style={{ color: MUTED }}>
                ₹1L
              </span>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Ad Copy / Message
            </label>
            <textarea
              className="odc-input min-h-[6rem] w-full resize-y"
              rows={4}
              placeholder="Write your ad message or campaign brief here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
              Primary Goal
            </label>
            <div className="flex flex-wrap gap-2">
              {GOALS.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGoal(g)}
                  style={{ ...pillBase, ...(goal === g ? pillActive : {}) }}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="odc-btn-primary w-full justify-center !py-3"
            disabled={busy}
            onClick={() => void simulateCampaign()}
          >
            {busy ? 'Simulating... ⏳' : '🧪 Simulate Campaign'}
          </button>
        </div>

        {/* RIGHT — Results */}
        <div className="odc-card !flex !min-h-[320px] !flex-col !p-7" style={{ transform: 'none' }}>
          <h2 className="mb-4 text-lg font-bold" style={{ color: TEXT }}>
            Simulation Results
          </h2>

          {!busy && !results ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
              <span className="text-5xl">🧪</span>
              <p className="text-sm font-semibold" style={{ color: TEXT }}>
                Run a simulation to see predicted results
              </p>
              <p className="max-w-xs text-xs" style={{ color: MUTED }}>
                Results appear here after simulation
              </p>
            </div>
          ) : null}

          {busy ? (
            <div className="flex flex-1 flex-col gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-14 w-full rounded-xl"
                  style={{
                    background: 'linear-gradient(90deg, var(--card2), var(--border), var(--card2))',
                    backgroundSize: '200% 100%',
                    animation: 'odc-shimmer 1.2s ease infinite',
                  }}
                />
              ))}
            </div>
          ) : null}

          {!busy && results ? (
            <div className="flex flex-1 flex-col gap-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="odc-card !p-4" style={{ transform: 'none', background: BG, borderColor: BORDER }}>
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: MUTED }}>
                    <span>👁️</span> Predicted Reach
                  </div>
                  <p className="odc-metric mt-2 !text-xl" style={{ color: ACCENT }}>
                    {results.predicted_reach.toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="odc-card !p-4" style={{ transform: 'none', background: BG, borderColor: BORDER }}>
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: MUTED }}>
                    <span>👥</span> Expected Leads
                  </div>
                  <p className="odc-metric mt-2 !text-xl" style={{ color: CYAN }}>
                    {results.expected_leads}
                  </p>
                </div>
                <div className="odc-card !p-4" style={{ transform: 'none', background: BG, borderColor: BORDER }}>
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: MUTED }}>
                    <span>💰</span> Cost Per Lead
                  </div>
                  <p className="odc-metric mt-2 !text-xl" style={{ color: PURPLE }}>
                    ₹{results.cost_per_lead.toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="odc-card !p-4" style={{ transform: 'none', background: BG, borderColor: BORDER }}>
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: MUTED }}>
                    <span>🎯</span> Success Rate
                  </div>
                  <p className="odc-metric mt-2 !text-xl" style={{ color: ACCENT }}>
                    {Math.round(results.success_probability)}%
                  </p>
                </div>
              </div>

              <div
                className="odc-badge inline-flex w-fit rounded-lg !px-3 !py-2 !text-xs font-bold"
                style={{
                  background: `${ACCENT}22`,
                  color: ACCENT,
                  border: `1px solid ${ACCENT}55`,
                }}
              >
                🏆 Best Channel: {results.best_channel}
              </div>

              <p className="text-xs italic leading-relaxed" style={{ color: MUTED }}>
                Based on your {formatINR(budget)} budget targeting{' '}
                {audience.trim() || 'your described audience'}, we predict {results.expected_leads} leads at ₹
                {results.cost_per_lead.toLocaleString('en-IN')} per lead. {goal} campaigns on {results.best_channel}{' '}
                perform best for this audience profile.
              </p>

              <p className="mt-auto text-[10px] leading-snug" style={{ color: MUTED }}>
                *Predictions based on AI models. Actual results may vary.
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {/* Recent Simulations */}
      <section>
        <h2 className="mb-4 text-lg font-bold" style={{ color: TEXT }}>
          Recent Simulations
        </h2>
        <div
          className="odc-card flex flex-col items-center justify-center gap-2 !py-16"
          style={{ transform: 'none' }}
        >
          <span className="text-4xl">🧪</span>
          <p className="text-sm font-semibold" style={{ color: TEXT }}>
            No simulations yet
          </p>
          <p className="text-xs" style={{ color: MUTED }}>
            Your simulation history will appear here
          </p>
        </div>
      </section>

      <style>{`
        @keyframes odc-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
